import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileText, Search, AlertTriangle, ClipboardCheck, Save } from "lucide-react";
import { PageHeader } from "@/components/common";
import EvaluasiRekap from "./EvaluasiRekap";

const TWS = [
  { v: "1", l: "Triwulan I", sub: "Jul–Sep" },
  { v: "2", l: "Triwulan II", sub: "Okt–Des" },
  { v: "3", l: "Triwulan III", sub: "Jan–Mar" },
  { v: "4", l: "Kelulusan", sub: "rata-rata 12 bulan" },
];

export default function EvaluasiTriwulan() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [view, setView] = useState("rekap");
  const [tw, setTw] = useState("1");
  const [rib, setRib] = useState("all");
  const [dep, setDep] = useState("all");
  const [q, setQ] = useState("");
  const [amb, setAmb] = useState("");
  const params = { tw, ribath: rib === "all" ? "" : rib, departemen: dep === "all" ? "" : dep, q: q || "" };
  const { data } = useQuery({ queryKey: ["evaluasi", params], queryFn: async () => (await api.get("/evaluasi/triwulan", { params })).data, keepPreviousData: true });
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });

  const rows = data?.rows || [];
  const r = data?.ringkasan || {};
  const ambang = data?.ambang ?? 75;

  const saveAmb = async () => {
    const v = parseInt(amb, 10);
    if (!v || v < 1 || v > 100) return toast.error("Ambang batas harus 1–100");
    try { await api.put("/evaluasi/config", { ambang: v }); toast.success(`Ambang batas disimpan: ${v}%`); setAmb(""); qc.invalidateQueries({ queryKey: ["evaluasi"] }); }
    catch { toast.error("Gagal menyimpan ambang batas"); }
  };
  const exp = (fmt) => downloadFile(
    `/evaluasi/triwulan/export?tw=${tw}&format=${fmt}&scope=below&ribath=${encodeURIComponent(params.ribath)}&departemen=${encodeURIComponent(params.departemen)}`,
    `Evaluasi_TW${tw}_dibawah_${ambang}.${fmt === "excel" ? "xlsx" : "pdf"}`);

  const rowStyle = (s) => s === "below" ? "border-l-4 border-rose-500 bg-rose-50/50"
    : s === "above" ? "border-l-4 border-emerald-500"
    : s === "invalid" ? "border-l-4 border-amber-500 bg-amber-50/50" : "border-l-4 border-slate-300";
  const statusText = (row) => row.status === "below" ? { t: "Di bawah ambang", c: "text-rose-600" }
    : row.status === "above" ? { t: "Memenuhi", c: "text-emerald-600" }
    : row.status === "invalid" ? { t: "Data tidak valid, perlu diperbaiki di sumber", c: "text-amber-600" }
    : { t: "Belum ada data", c: "text-slate-400" };

  return (
    <div className="space-y-6" data-testid="evaluasi-page">
      <PageHeader icon={ClipboardCheck} title="Evaluasi Triwulan" subtitle="Rekap nilai keaktifan per bulan, Triwulan & Kelulusan, serta penjaringan memorandum." />

      {/* View toggle */}
      <div className="inline-flex rounded-xl bg-emerald-100/60 dark:bg-white/5 p-1" data-testid="evaluasi-view-toggle">
        <button data-testid="evaluasi-view-rekap" onClick={() => setView("rekap")}
          className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${view === "rekap" ? "bg-emerald-700 text-white shadow" : "text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50"}`}>Rekap Nilai</button>
        <button data-testid="evaluasi-view-penjaringan" onClick={() => setView("penjaringan")}
          className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${view === "penjaringan" ? "bg-emerald-700 text-white shadow" : "text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50"}`}>Penjaringan Memorandum</button>
      </div>

      {view === "rekap" ? <EvaluasiRekap /> : (
      <>
      {/* Triwulan selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="evaluasi-tw-selector">
        {TWS.map((t) => (
          <button key={t.v} data-testid={`evaluasi-tw-${t.v}`} onClick={() => setTw(t.v)}
            className={`rounded-2xl p-4 text-left transition-all ${tw === t.v ? "bg-emerald-700 text-white shadow-md" : "glass text-slate-700 dark:text-slate-200 hover:bg-emerald-50"}`}>
            <p className="font-bold">{t.l}</p><p className={`text-xs ${tw === t.v ? "text-lime-100" : "text-slate-400"}`}>{t.sub}</p>
          </button>
        ))}
      </div>

      {/* Ambang batas + ringkasan */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="glass rounded-2xl p-5 md:col-span-2" data-testid="evaluasi-ringkasan">
          <p className="text-sm text-slate-500 dark:text-slate-300 mb-2">{data?.label} · ambang batas <b className="text-emerald-800 dark:text-emerald-300">{ambang}%</b></p>
          <div className="flex items-end gap-5 flex-wrap">
            <div><p className="text-4xl font-extrabold text-rose-600" data-testid="evaluasi-di-bawah">{r.di_bawah ?? 0}</p><p className="text-xs text-slate-500 dark:text-slate-300">di bawah (salah satu)</p></div>
            <div><p className="text-2xl font-bold text-rose-500" data-testid="evaluasi-di-bawah-dep">{r.di_bawah_dep ?? 0}</p><p className="text-xs text-slate-500 dark:text-slate-300">Departemen</p></div>
            <div><p className="text-2xl font-bold text-rose-500" data-testid="evaluasi-di-bawah-ribath">{r.di_bawah_ribath ?? 0}</p><p className="text-xs text-slate-500 dark:text-slate-300">Ribath</p></div>
            <div><p className="text-2xl font-bold text-slate-600">{r.total ?? 0}</p><p className="text-xs text-slate-500 dark:text-slate-300">total aktif</p></div>
          </div>
          {(r.invalid > 0 || r.nodata > 0) && (
            <p className="text-[11px] text-amber-600 mt-3 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{r.invalid || 0} data tidak valid · {r.nodata || 0} belum ada data</p>
          )}
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Ambang Batas</p>
          {isSuper ? (
            <div className="flex gap-2">
              <Input data-testid="evaluasi-ambang-input" inputMode="numeric" placeholder={`${ambang}`} value={amb} onChange={(e) => setAmb(e.target.value.replace(/\D/g, ""))} className="h-10 rounded-xl bg-white/70 dark:bg-white/5" />
              <Button data-testid="evaluasi-ambang-save" onClick={saveAmb} className="h-10 rounded-xl bg-emerald-700 hover:bg-emerald-800 gap-1"><Save className="h-4 w-4" /></Button>
            </div>
          ) : <p className="text-2xl font-extrabold text-emerald-900 dark:text-emerald-100">{ambang}%</p>}
          <p className="text-[11px] text-slate-400 mt-2">{isSuper ? "Ubah sesuai kebijakan pondok (default 75%)." : "Hanya Super Admin yang dapat mengubah."}</p>
        </div>
        <div className="glass rounded-2xl p-5 flex flex-col justify-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Ekspor Memorandum</p>
          <div className="flex gap-2">
            <Button data-testid="evaluasi-export-pdf" onClick={() => exp("pdf")} variant="outline" className="rounded-xl bg-white/60 dark:bg-white/5 gap-2 flex-1"><FileText className="h-4 w-4" /> PDF</Button>
            <Button data-testid="evaluasi-export-excel" onClick={() => exp("excel")} variant="outline" className="rounded-xl bg-white/60 dark:bg-white/5 gap-2 flex-1"><FileDown className="h-4 w-4" /> Excel</Button>
          </div>
          <p className="text-[11px] text-slate-400">Daftar santri di bawah {ambang}% pada {data?.label}.</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
          <Input data-testid="evaluasi-search" placeholder="Cari nama / no induk..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full sm:w-56 rounded-xl bg-white/60 dark:bg-white/5" /></div>
        <Select value={rib} onValueChange={setRib}><SelectTrigger className="w-40 rounded-xl bg-white/60 dark:bg-white/5" data-testid="evaluasi-ribath-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Ribath</SelectItem>{(options.ribath || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        <Select value={dep} onValueChange={setDep}><SelectTrigger className="w-44 rounded-xl bg-white/60 dark:bg-white/5" data-testid="evaluasi-departemen-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Departemen</SelectItem>{(options.departemen || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
      </div>

      {/* Tabel */}
      <div className="glass rounded-2xl p-3 sm:p-4 overflow-x-auto" data-testid="evaluasi-table">
        <div className="min-w-[560px] space-y-1.5">
          <div className="grid grid-cols-[36px_1fr_84px_84px] gap-2 px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-300">
            <span>No</span><span>Nama / No. Induk</span><span className="text-right">% Dept</span><span className="text-right">% Ribath</span>
          </div>
          {rows.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">Tidak ada santri untuk filter ini.</p>}
          {rows.map((row, i) => {
            const st = statusText(row);
            const pc = (s) => s === "below" ? "text-rose-600" : s === "above" ? "text-emerald-700" : "text-slate-400";
            const fmt = (v) => v == null ? "–" : `${v}%`;
            return (
              <motion.div key={row.induk || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.01, 0.3) }}
                className={`grid grid-cols-[36px_1fr_84px_84px] gap-2 items-center px-3 py-2.5 rounded-lg bg-white/50 dark:bg-white/5 ${rowStyle(row.status)}`} data-testid={`evaluasi-row-${row.induk}`}>
                <span className="text-slate-400 text-sm">{row.no || i + 1}</span>
                <div className="min-w-0"><p className="font-semibold text-emerald-950 dark:text-emerald-50 truncate text-sm">{row.nama}</p><p className="text-xs text-slate-400 truncate">{row.induk} · {row.departemen || "-"} · {row.ribath || "-"} · <span className={st.c}>{st.t}</span></p></div>
                <span className={`text-right font-extrabold ${pc(row.status_dep)}`} data-testid={`eval-dep-${row.induk}`}>{fmt(row.persen_dep)}</span>
                <span className={`text-right font-extrabold ${pc(row.status_rib)}`} data-testid={`eval-rib-${row.induk}`}>{fmt(row.persen_rib)}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
